import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["as", "buttonType", "children", "fluid", "icon", "iconPosition", "iconSize", "size", "disabled", "onClick"];
import PropTypes from 'prop-types';
import React, { forwardRef, useContext } from 'react';
import { BUTTON_SIZES, BUTTON_TYPES } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { ButtonGroupContext } from '../ButtonGroup/ButtonGroup.context';
import { ButtonIcon } from '../ButtonIcon/ButtonIcon';
import { StyledButton } from './Button.styles';

/**
 * @since 1.0.5
 */
export var Button = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var as = _ref.as,
    buttonType = _ref.buttonType,
    children = _ref.children,
    fluid = _ref.fluid,
    icon = _ref.icon,
    iconPosition = _ref.iconPosition,
    iconSize = _ref.iconSize,
    size = _ref.size,
    disabled = _ref.disabled,
    onClick = _ref.onClick,
    rest = _objectWithoutProperties(_ref, _excluded);
  var context = useContext(ButtonGroupContext);
  var buttonProps = Object.assign({
    as: as,
    buttonType: context.buttonType || buttonType,
    fluid: context.fluid || fluid,
    size: context.size || size,
    disabled: disabled,
    onClick: disabled ? null : onClick,
    ref: forwardedRef
  }, rest);
  var accessibilityProps = {
    'aria-disabled': disabled
  };
  if (disabled) {
    accessibilityProps.tabIndex = -1;
  }

  // Inputs do not allow icon children
  if (as === 'input') {
    return /*#__PURE__*/React.createElement(StyledButton, buttonProps);
  }
  var buttonIconProps = {
    children: icon,
    iconPosition: iconPosition,
    iconSize: iconSize
  };
  return /*#__PURE__*/React.createElement(StyledButton, _extends({}, accessibilityProps, buttonProps), icon && iconPosition === 'start' ? /*#__PURE__*/React.createElement(ButtonIcon, buttonIconProps) : null, children, icon && iconPosition === 'end' ? /*#__PURE__*/React.createElement(ButtonIcon, buttonIconProps) : null);
});
Button.displayName = 'Button';
Button.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node,
  /**
   * Set the button as disabled.
   */
  disabled: PropTypes.bool,
  /**
   * A fluid button will take up all horizontal space.
   * Can be inherited from a parent ButtonGroup.
   *
   * Supports inline media query objects
   */
  fluid: CustomPropTypes.inlineMediaQuery(PropTypes.bool),
  /**
   * Add an `Icon` to the button.
   */
  icon: PropTypes.node,
  /**
   * Change the position of the `icon`.
   */
  iconPosition: PropTypes.oneOf(['start', 'end']),
  /**
   * This is not an exposed API property. It is only used internally.
   *
   * @ignore
   */
  iconSize: PropTypes.string,
  /**
   * Inverse colors for use on dark or colored backgrounds.
   */
  inverse: PropTypes.bool,
  /**
   * The size of the button.
   * Can be inherited from a parent ButtonGroup.
   */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  /**
   * The type of the button.
   * Can be inherited from a parent ButtonGroup.
   */
  buttonType: PropTypes.oneOf(Object.values(BUTTON_TYPES)),
  /**
   * Use the styled-components
   * [polymorphic "as" prop](https://www.styled-components.com/docs/api#as-polymorphic-prop)
   * to change the HTML tag of the component.
   *
   * This can be used to apply `Button` styling to HTML anchors, inputs, and custom components.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  as: PropTypes.any
} : {};
Button.defaultProps = {
  children: undefined,
  disabled: undefined,
  fluid: false,
  icon: undefined,
  iconPosition: 'start',
  iconSize: 'sm',
  inverse: false,
  size: BUTTON_SIZES.md,
  buttonType: BUTTON_TYPES.secondary,
  as: 'button'
};